import SpinnerService from '@/services/spinner.service';
import FederatedAuthConfig, {
  ProviderData,
  PopupOptions,
} from '@/models/entities/federated-auth-config';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { Configuration } from '@/types/configuration.d';
import SocialProvider, { SOCIAL_PROVIDER_TYPE } from '@/models/entities/social-provider';

interface ProviderResponse {
  state?: string;
  code?: string;
  // eslint-disable-next-line camelcase
  id_token?: string;
  [key: string]: any;
}

interface BroadcastChannelParams {
  code?: number;
  statusCode?: number;
  error?: string;
  errors?: Record<string, any>;
  success?: boolean;
  data?: any;
}

const isFederatedLoginEnabled = (configuration?: Configuration) => {
  return (
    configuration.allowFederatedLogin &&
    configuration.socialProviders?.some(
      sp => sp.auth && Object.values(SOCIAL_PROVIDER_TYPE).includes(sp.provider),
    )
  );
};

const getFullUrlPath = (location: Location): string => {
  return `${location.protocol}//${location.hostname}:${location.port || '443'}${
    location.pathname.startsWith('/') ? location.pathname : `/${location.pathname}`
  }`;
};

const parseQueryString = (str: string): Record<string, string | boolean> => {
  const urlSearchParams = new URLSearchParams(str);
  return Object.fromEntries(urlSearchParams.entries());
};

/**
 * Creates an AxiosError error object from provider response parameters.
 * This ensures BroadcastChannel errors have the same structure as axios errors,
 * allowing consistent error handling for validation errors, verification required, etc.
 */
const createAxiosError = (params: BroadcastChannelParams): AxiosError => {
  const response = {
    data: {
      message: params.error,
      code: params.code,
      error: params.error,
      errors: params.errors,
      data: params.data,
    },
    status: params.statusCode ?? 400,
    statusText: 'Bad Request',
    headers: {},
    config: {} as any,
  };
  return new AxiosError(params.error, null, {} as any, null, response);
};

const THREE_MINUTES_AS_MS = 180000;
const setupBroadcastChannelListener = (
  resolve: (value: ProviderResponse) => void,
  reject: (reason: Error) => void,
  popup: Window | null,
): (() => void) => {
  if (typeof BroadcastChannel === 'undefined') {
    console.warn('BroadcastChannel not supported in this browser');
    return () => {};
  }

  const channel = new BroadcastChannel('oauth_channel');
  let fallbackTimeout: ReturnType<typeof setTimeout> | null = null;

  const clearFallbackTimeout = () => {
    if (fallbackTimeout) {
      clearTimeout(fallbackTimeout);
      fallbackTimeout = null;
    }
  };

  const startFallbackTimeout = () => {
    if (!fallbackTimeout) {
      fallbackTimeout = setTimeout(() => {
        reject(new Error('OAuth authentication timeout'));
        channel.close();
      }, THREE_MINUTES_AS_MS);
    }
  };

  const messageHandler = (event: MessageEvent) => {
    if (!event.data || typeof event.data !== 'object') {
      console.debug('OAuth channel received non-object message, ignoring', event.data);
      return;
    }

    if (event.data.type === 'oauth_response') {
      const params = event.data.params;

      if (params.error) {
        const axiosError = createAxiosError(params);
        reject(axiosError);
        if (popup && !popup.closed) {
          popup.close();
        }
        clearFallbackTimeout();
        channel.close();
        return;
      }

      if (popup && !popup.closed) {
        popup.close();
      }

      clearFallbackTimeout();
      channel.close();
      resolve(params);
      return;
    }

    if (event.data.type === 'window_closing') {
      clearFallbackTimeout();
      reject(new Error('The popup window was closed'));
      channel.close();
    }
  };

  channel.addEventListener('message', messageHandler);

  startFallbackTimeout();

  return () => {
    clearFallbackTimeout();
    channel.removeEventListener('message', messageHandler);
    channel.close();
  };
};

const pollPopupWindow = (
  popup: Window | null,
  polling: ReturnType<typeof setInterval>,
  reject: (reason: Error) => void,
  redirectUriPath: string,
  resolve: (value: ProviderResponse) => void,
): void => {
  if (!popup || popup.closed || popup.closed === undefined) {
    clearInterval(polling);
    reject(new Error('The popup window was closed'));
    return;
  }

  const popupWindowPath = getFullUrlPath(popup.location);
  if (popupWindowPath !== redirectUriPath) {
    // Don't reject here - just return and wait for the next polling cycle
    return;
  }

  if (popup.location.search || popup.location.hash) {
    const query = parseQueryString(popup.location.search.substring(1).replace(/\/$/, ''));
    const hash = parseQueryString(popup.location.hash.substring(1).replace(/\[\/$]/, ''));
    const params: ProviderResponse = { ...query, ...hash };

    if (params.error) {
      reject(new Error(params.error));
      popup.close();
      clearInterval(polling);
      return;
    }

    popup.close();
    clearInterval(polling);
    resolve(params);
    return;
  }

  popup.close();
  clearInterval(polling);

  reject(
    new Error(
      'OAuth redirect has occurred but no query or hash parameters were found. ' +
        'They were either not set during the redirect, or were removed.',
    ),
  );
};

const FIVE_MINUTES_AS_MS = 300000;
const popupPoll = (
  redirectUri: string,
  popup: Window,
): Promise<ProviderResponse> | Promise<AxiosResponse> => {
  const redirectUriParser = document.createElement('a');
  redirectUriParser.href = redirectUri;
  const redirectUriPath = getFullUrlPath((redirectUriParser as unknown) as Location);

  return new Promise<ProviderResponse>((resolve, reject) => {
    let cleanupBroadcastListener: (() => void) | null = null;
    let polling: ReturnType<typeof setInterval> | null = null;
    let fallbackTimeout: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (cleanupBroadcastListener) {
        cleanupBroadcastListener();
        cleanupBroadcastListener = null;
      }
      if (polling) {
        clearInterval(polling);
        polling = null;
      }
      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout);
        fallbackTimeout = null;
      }
    };

    const wrappedResolve = (value: ProviderResponse) => {
      cleanup();
      resolve(value);
    };

    const wrappedReject = (error: Error) => {
      cleanup();
      reject(error);
    };

    cleanupBroadcastListener = setupBroadcastChannelListener(wrappedResolve, wrappedReject, popup);

    polling = setInterval(() => {
      pollPopupWindow(popup, polling, wrappedReject, redirectUriPath, wrappedResolve);
    }, 500);

    fallbackTimeout = setTimeout(() => {
      if (polling || cleanupBroadcastListener) {
        wrappedReject(new Error('OAuth authentication timeout'));
      }
    }, FIVE_MINUTES_AS_MS);
  });
};

const tokenExchange = (
  providerData: ProviderData,
  oauthResponse: ProviderResponse,
  currentNonce: string,
  stayLoggedIn: boolean,
  recaptcha?: string,
  reauthentication?: boolean,
): Promise<AxiosResponse> => {
  const payload: Record<string, string | boolean> = {};

  Object.entries(providerData.responseParams).forEach(([key, value]) => {
    switch (value) {
      case 'code':
        if (oauthResponse.code) {
          payload[key] = oauthResponse.code;
        }
        break;
      case 'clientId':
        payload[key] = providerData.clientId;
        break;
      case 'redirectUri':
        payload[key] = providerData.redirectUri;
        break;
      case 'id_token':
        payload[key] = oauthResponse.id_token;
        break;
      case 'nonce':
        payload[key] = currentNonce;
        break;
      default:
        if (providerData[key]) {
          payload[value] = providerData[key];
        }
    }
  });

  if (oauthResponse.state) {
    payload.state = oauthResponse.state;
  }

  if (providerData.name === SOCIAL_PROVIDER_TYPE.FACEBOOK) {
    payload.stayLoggedInFacebook = stayLoggedIn;
  }

  if (recaptcha) {
    payload.recaptcha = recaptcha;
  }

  if (reauthentication) {
    payload.loginOnly = true;
  }

  return axios.post(providerData.url, payload);
};

const createAuthPromise = (
  providerData: ProviderData,
  popup: Window,
  currentState: string,
  currentNonce: string,
  stayLoggedIn: boolean,
  recaptcha?: string,
  reauthentication?: boolean,
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    popupPoll(providerData.redirectUri, popup)
      .then(providerResponse => {
        if (providerResponse.state && providerResponse.state !== currentState) {
          reject(
            new Error(
              'The value returned in the state parameter does not match the state value from your original authorization code request.',
            ),
          );
          return;
        }
        if (providerData.responseMode === 'form_post') {
          // This would have been handled on the backend already, no need to token exchange
          resolve(providerResponse);
          return;
        }
        resolve(
          tokenExchange(
            providerData,
            providerResponse,
            currentNonce,
            stayLoggedIn,
            recaptcha,
            reauthentication,
          ),
        );
      })
      .catch(error => {
        reject(error instanceof Error ? error : new Error(String(error)));
      });
  });
};

const camelCase = (name: string): string => {
  return name.replace(/([:\-_]+(.))/g, (_, separator, letter, offset) => {
    return offset ? letter.toUpperCase() : letter;
  });
};

const stringifyOptions = (options: PopupOptions): string => {
  const parts: string[] = [];
  Object.entries(options).forEach(([key, value]) => {
    parts.push(`${key}=${value}`);
  });
  return parts.join(',');
};

function getPromptValue(stayLoggedIn: boolean, reauthentication: boolean) {
  if (stayLoggedIn) {
    return 'consent';
  }
  if (reauthentication) {
    return 'login';
  }
  return null;
}

function getScopeValue(paramValue: string[], providerData: ProviderData) {
  let scope = paramValue.join(providerData.scopeDelimiter);
  if (providerData.scopePrefix) {
    scope = [providerData.scopePrefix, ...paramValue].join(providerData.scopeDelimiter);
  }
  return scope;
}

/**
 * Starts a social/oauth login using the specified provider.
 * @param name The oauth provider (facebook,google,etc.)
 * @param login If this request is for a login
 * @param stayLoggedIn If a refresh token should be requested to stay logged in
 * @param configuration Configuration object from the app
 * @param recaptcha reCAPTCHA token if available
 * @param reauthentication If true, this is a reauthentication attempt
 * @param postLoginCallback Function to call after successful login
 * @returns {Promise<any>}
 */
const performFederatedLogin = async (
  name: string,
  login?: boolean,
  stayLoggedIn?: boolean,
  configuration?: Configuration,
  recaptcha?: string,
  reauthentication?: boolean,
  postLoginCallback?: () => void,
): Promise<any> => {
  const siteUrl =
    process.env.NODE_ENV === 'production' ? configuration?.siteUrl : window.location.origin;
  // Find the provider data
  const providerData = FederatedAuthConfig.get(name, siteUrl) as ProviderData | undefined;
  if (!providerData) {
    return Promise.reject(new Error(`Invalid Social Provider: ${name}`));
  }

  // Get the settings configured for the login provider
  const providerConfig = configuration?.socialProviders?.find(ps => {
    return ps.provider === name;
  });
  if (providerConfig?.authId) {
    providerData.clientId = providerConfig.authId;
  }

  // Generate state and nonce from the backend
  let currentState = '';
  let currentNonce = '';

  if (providerData.nonceGenerationEndpoint) {
    let stayLoggedInApple = false;
    if (providerData.name === SOCIAL_PROVIDER_TYPE.APPLE) {
      stayLoggedInApple = stayLoggedIn;
    }

    const response = await axios.post<{ state: string; nonce: string }>(
      providerData.nonceGenerationEndpoint,
      {
        recaptcha,
        reauthentication,
        stayLoggedInApple,
      },
    );

    currentState = response.data.state;
    currentNonce = response.data.nonce;
  }

  // Build the URL to start the oauth flow
  const keyValuePairs: Array<[string, string]> = [];
  const urlParamsCategories = ['defaultUrlParams', 'requiredUrlParams', 'optionalUrlParams'];
  urlParamsCategories.forEach(paramsCategory => {
    providerData[paramsCategory]?.forEach(paramName => {
      const camelizedName = camelCase(paramName);
      let paramValue =
        typeof providerData[paramName] === 'function'
          ? providerData[paramName]()
          : providerData[camelizedName];
      if (paramName === 'redirect_uri' && !paramValue) {
        return;
      }
      if (paramName === 'state') {
        paramValue = encodeURIComponent(currentState);
      }
      if (paramName === 'nonce') {
        paramValue = encodeURIComponent(currentNonce);
      }
      if (paramName === 'scope' && Array.isArray(paramValue)) {
        paramValue = getScopeValue(paramValue, providerData);
      }
      if (paramName === 'access_type') {
        paramValue = stayLoggedIn ? 'offline' : null;
      }
      if (paramName === 'prompt') {
        paramValue = getPromptValue(stayLoggedIn, reauthentication);
      }
      keyValuePairs.push([paramName, paramValue]);
    });
  });

  const url = [
    providerData.authorizationEndpoint,
    keyValuePairs
      .filter(pair => pair[1])
      .map(pair => {
        return `${encodeURIComponent(pair[0])}=${encodeURIComponent(pair[1])}`;
      })
      .join('&'),
  ].join('?');

  // Open and focus the popup window
  const width = providerData.popupOptions.width || 500;
  const height = providerData.popupOptions.height || 500;
  const options = stringifyOptions({
    width,
    height,
    top: window.screenY + (window.outerHeight - height) / 2.5,
    left: window.screenX + (window.outerWidth - width) / 2,
  });

  const popupName = window.navigator.userAgent.indexOf('CriOS') > -1 ? '_blank' : name;
  const popup = window.open(url, popupName, options);
  if (popup?.focus) {
    popup.focus();
  }

  // Block UI and resolve the login
  SpinnerService.open();
  return new Promise((resolve, reject) => {
    createAuthPromise(
      providerData,
      popup,
      currentState,
      currentNonce,
      stayLoggedIn,
      recaptcha,
      reauthentication,
    )
      .then(
        success => {
          if (login !== false && postLoginCallback) {
            postLoginCallback();
          }
          resolve(success);
        },
        (error: Error) => {
          reject(error);
        },
      )
      .finally(() => {
        SpinnerService.close();
      });
  });
};

const socialAuthLabel = (provider: SocialProvider) => {
  switch (provider.provider) {
    case 'google':
      return 'Google';
    case 'facebook':
      return 'Facebook';
    case 'apple':
      return 'Apple';
    default:
      return provider.provider;
  }
};

export default () => {
  return {
    isFederatedLoginEnabled,
    performFederatedLogin,
    getFullUrlPath,
    stringifyOptions,
    camelCase,
    parseQueryString,
    socialAuthLabel,
  };
};
